import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plane, Truck, Bike, Train, Bus, Star, ArrowUpDown, Route, BellRing, BellOff,
  Timer, CheckCircle2, Sparkles, Loader2, RefreshCw, ShieldCheck, ShieldAlert, Send, CalendarDays, CreditCard,
} from "lucide-react";
import Logo from "@/components/Logo";
import { SenderVerifyCard } from "@/components/SenderVerify";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { sizeLabel } from "@/lib/sizes";

const MODE_ICON = { car: Truck, suv: Truck, bike: Bike, train: Train, flight: Plane, bus: Bus };
const MODE_LABEL = { bike: "Bike / Scooter", car: "Car", suv: "SUV / Tempo", bus: "Bus", train: "Train", flight: "Flight" };
const MAX_REQUESTS = 4;

const ModeIcon = ({ m }) => { const Icon = MODE_ICON[m] || Truck; return <Icon className="w-3.5 h-3.5"/>; };

const secsLeft = (iso) => (iso ? Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000)) : 0);
const fmtClock = (secs) => (secs <= 0 ? "expired" : `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`);

const VerifiedBadge = ({ carrier }) => (carrier?.verified ? (
  <Badge className="bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid="carrier-verified">
    <ShieldCheck className="w-3 h-3"/> Verified
  </Badge>
) : (
  <Badge className="bg-slate-100 text-slate-600 inline-flex items-center gap-1" data-testid="carrier-unverified">
    <ShieldAlert className="w-3 h-3"/> Not verified
  </Badge>
));

const MatchedNotice = ({ delivery }) => {
  const pay = delivery.status === "awaiting_payment";
  return (
    <div className={`rounded-2xl border-2 p-5 ${pay ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid={pay ? "pay-now-banner" : "parcel-matched-notice"}>
      <div className="flex items-start gap-3">
        {pay ? <Timer className="w-6 h-6 text-amber-700 shrink-0"/> : <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0"/>}
        <div className="flex-1">
          <div className="font-display font-bold">{pay ? `${delivery.carrier?.name || "A carrier"} accepted — complete payment` : "Matched & on the way"}</div>
          <div className="text-sm text-slate-600 mt-0.5">
            {pay ? "Payment is taken only now that a carrier has accepted. Pay within 15 minutes to confirm." : "This parcel has a carrier. Track it from the delivery screen."}
          </div>
          <Button asChild className="mt-3 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="go-to-delivery-btn">
            <Link to={`/delivery/${delivery.id}`}>{pay ? <><CreditCard className="w-4 h-4 mr-1"/> Go to payment</> : "Open delivery"}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ parcel, onRefresh, loading }) => {
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/parcels/${parcel.id}/notify-me`).then((r) => setActive(!!r.data?.active)).catch(() => {});
  }, [parcel.id]);
  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post(`/parcels/${parcel.id}/notify-me`, {});
      setActive(true);
      toast.success("We'll notify you the moment a matching traveller posts a trip.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't set up the alert."); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl bg-white border p-8 text-center" data-testid="no-carrier-msg">
      <div className="flex justify-center mb-3"><Logo size={48} opacity={0.35}/></div>
      <div className="font-display font-bold text-slate-900 mb-1">No travellers on this route yet — check back soon</div>
      <div className="text-sm text-slate-600 max-w-md mx-auto mb-4">
        We only list travellers whose trip passes close enough to your pickup and drop. New trips are posted all the time
        {parcel.delivery_by ? <> — we're looking for trips before <b>{new Date(parcel.delivery_by).toLocaleDateString()}</b></> : null}.
      </div>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Button variant="outline" className="rounded-full" onClick={onRefresh} disabled={loading} data-testid="empty-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}/> Check again
        </Button>
        <Button data-testid="notify-me-btn" disabled={busy || active} onClick={subscribe}
                className={`rounded-full ${active ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-900 hover:bg-slate-800"}`}>
          {active ? <><BellRing className="w-4 h-4 mr-1"/> We'll notify you</> : <><BellOff className="w-4 h-4 mr-1"/> Notify me when a match posts</>}
        </Button>
      </div>
    </div>
  );
};

const CarrierCard = ({ trip, selected, canSelect, onToggle, onCancel }) => {
  const quote = Number(trip.quote?.sender_total || 0);
  const req = trip.my_request && trip.my_request.status === "requested" ? trip.my_request : null;
  const c = trip.carrier || {};
  const vehicleName = ["car", "suv", "bike"].includes(trip.travel_mode) && trip.vehicle?.make_model ? ` · ${trip.vehicle.make_model}` : "";
  return (
    <div data-testid={`trip-card-${trip.id}`}
         className={`rounded-2xl bg-white border-2 p-4 transition-colors ${selected ? "border-amber-500 bg-amber-50/40" : req ? "border-amber-200" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-start gap-3">
        {!req && (
          <Checkbox data-testid={`select-trip-${trip.id}`} checked={selected} disabled={!selected && !canSelect}
                    onCheckedChange={onToggle} className="mt-1"/>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-display font-bold text-slate-900" data-testid={`carrier-name-${trip.id}`}>{c.name || "Traveller"}</span>
            <VerifiedBadge carrier={c}/>
            {c.rating_avg > 0 && <span className="text-xs text-slate-500 inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 stroke-amber-500"/> {c.rating_avg}</span>}
          </div>
          <div className="mt-1 text-sm text-slate-800 truncate" data-testid={`trip-route-${trip.id}`}>{trip.origin} → {trip.destination}</div>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5" data-testid={`trip-vehicle-${trip.id}`}>
              <ModeIcon m={trip.travel_mode}/> {MODE_LABEL[trip.travel_mode] || trip.travel_mode}{vehicleName}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5" data-testid={`trip-date-${trip.id}`}>
              <CalendarDays className="w-3 h-3"/> {trip.date}{trip.departure_time ? ` · ${trip.departure_time}` : ""}
            </span>
            {typeof trip.detour_km === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5">
                <Route className="w-3 h-3"/> {trip.detour_km.toFixed(1)} km detour
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl font-extrabold text-slate-900" data-testid={`trip-price-${trip.id}`}>₹{quote.toFixed(0)}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">All inclusive</div>
        </div>
      </div>
      {req && (
        <div className="mt-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium px-2.5 py-1" data-testid={`requested-chip-${trip.id}`}>
            <Timer className="w-3 h-3"/> Requested · {fmtClock(secsLeft(req.expires_at))} left
          </span>
          <Button size="sm" variant="outline" onClick={onCancel} data-testid={`cancel-req-${trip.id}`}
                  className="rounded-full ml-auto text-rose-700 border-rose-200 hover:bg-rose-50">Cancel request</Button>
        </div>
      )}
    </div>
  );
};

const OfferCard = ({ delivery, onAccept, onDecline, busy }) => (
  <div className="relative rounded-2xl bg-blue-50 border-2 border-blue-200 p-4" data-testid={`offer-card-${delivery.id}`}>
    <div className="absolute -top-2 left-4 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
      <Sparkles className="w-3 h-3"/> Offer from carrier
    </div>
    <div className="flex items-center justify-between gap-3 mt-1">
      <div className="min-w-0">
        <div className="font-display font-bold text-slate-900 truncate">{delivery.carrier?.name || "Carrier"} offered to deliver</div>
        <div className="text-xs text-slate-600 mt-0.5">
          {MODE_LABEL[delivery.trip?.travel_mode] || ""} · {delivery.trip?.origin} → {delivery.trip?.destination} · {delivery.trip?.date}
        </div>
        <div className="text-[11px] text-slate-600 mt-1 inline-flex items-center gap-1"><Timer className="w-3 h-3"/> expires in {fmtClock(secsLeft(delivery.expires_at))}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-2xl font-extrabold">₹{Number(delivery.sender_total || 0).toFixed(0)}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">All inclusive</div>
      </div>
    </div>
    <div className="mt-3 flex items-center gap-2">
      <Button variant="outline" size="sm" className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50" onClick={onDecline} data-testid={`offer-decline-${delivery.id}`}>Decline</Button>
      <Button size="sm" onClick={onAccept} disabled={busy} className="rounded-full ml-auto bg-blue-600 hover:bg-blue-700 text-white" data-testid={`offer-accept-${delivery.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Accept offer"}
      </Button>
    </div>
  </div>
);

const SendBar = ({ count, onSend, sending, left }) => (
  <div className="sticky bottom-3 z-20 rounded-2xl bg-slate-900 text-white p-3 pl-5 flex items-center gap-3 shadow-xl" data-testid="send-bar">
    <div className="text-sm flex-1">
      {count ? <><b data-testid="selected-count">{count}</b> of {left} selected</> : <span data-testid="selected-count-empty">Select up to {left} traveller{left === 1 ? "" : "s"}</span>}
    </div>
    <Button onClick={onSend} disabled={!count || sending} data-testid="send-requests-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
      {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send className="w-4 h-4 mr-1"/> {count ? `Send request${count > 1 ? "s" : ""} to ${count}` : "Send requests"}</>}
    </Button>
  </div>
);

const BrowseTrips = ({ parcel, onChanged }) => {
  const [trips, setTrips] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [sort, setSort] = useState("price");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [, setTick] = useState(0);
  const { user } = useAuth();
  const canContact = user?.sender_verification?.can_contact !== false;
  const [verifyPrompt, setVerifyPrompt] = useState(false);
  const promptVerify = () => {
    setVerifyPrompt(true);
    toast.info("Verify your identity to send your first parcel.");
    setTimeout(() => document.querySelector("[data-testid='sender-verify-card']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(iv); }, []);

  const refresh = useCallback(() => {
    if (!parcel?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/trips/for-parcel?parcel_id=${parcel.id}&sort=${sort}`).then((r) => r.data || []),
      api.get(`/deliveries?role=sender`).then((r) => (r.data || []).filter((d) => d.parcel_id === parcel.id)),
    ]).then(([t, d]) => {
      setTrips(t); setDeliveries(d); setLastRefreshed(new Date());
      setSelected((sel) => sel.filter((id) => t.some((x) => x.id === id && !(x.my_request && x.my_request.status === "requested"))));
    }).catch(() => toast.error("Couldn't load travellers. Try refreshing."))
      .finally(() => setLoading(false));
  }, [parcel?.id, sort]);

  useEffect(() => { refresh(); const iv = setInterval(refresh, 30000); return () => clearInterval(iv); }, [refresh]);

  const matched = useMemo(() => deliveries.find((d) => ["awaiting_payment", "accepted", "picked_up", "in_transit", "delivered"].includes(d.status)), [deliveries]);
  const live = deliveries.filter((d) => d.status === "requested" && d.initiated_by !== "carrier");
  const offers = deliveries.filter((d) => d.status === "requested" && d.initiated_by === "carrier");
  const left = Math.max(0, MAX_REQUESTS - live.length);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < left ? [...s, id] : s));
  const after = () => { refresh(); onChanged?.(); };

  const sendAll = async () => {
    if (!canContact) return promptVerify();
    setSending(true);
    try {
      const r = await api.post("/requests/bulk", { parcel_id: parcel.id, trip_ids: selected });
      const ok = r.data.sent.length, bad = r.data.errors;
      if (ok) toast.success(`Request sent to ${ok} traveller${ok > 1 ? "s" : ""}. First to accept gets the parcel. Requests expire in 30 minutes.`);
      bad.forEach((e) => toast.error(e.detail));
      setSelected([]); after();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the requests."); }
    setSending(false);
  };
  const act = async (id, path, msg) => {
    if (path === "accept" && !canContact) return promptVerify();
    setBusyId(id);
    try { await api.post(`/deliveries/${id}/${path}`, path === "decline" ? { reason: "" } : undefined); if (msg) toast.success(msg); after(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong."); }
    setBusyId(null);
  };

  if (matched) return <MatchedNotice delivery={matched}/>;

  return (
    <div className="space-y-3" data-testid="browse-travellers">
      <div className="rounded-2xl bg-slate-900 text-white p-5">
        <div className="text-xs text-slate-400 uppercase tracking-wider">Browse travellers</div>
        <div className="font-display font-bold text-lg">{parcel.pickup_location} → {parcel.drop_location}</div>
        <div className="text-sm text-slate-300" data-testid="browse-parcel-size">{sizeLabel(parcel)}{parcel.distance_km ? ` · ${Number(parcel.distance_km).toFixed(0)} km` : ""}</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="rounded-full" data-testid="browse-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
        {lastRefreshed && <span className="text-[11px] text-slate-500" data-testid="last-refreshed">Updated {lastRefreshed.toLocaleTimeString()}</span>}
        <div className="ml-auto flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-slate-500"/>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="rounded-full h-8 w-40 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="price">Price · low to high</SelectItem>
              <SelectItem value="rating">Rating · high to low</SelectItem>
              <SelectItem value="departure">Departure · soonest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="request-cap-hint">
        <Timer className="w-3 h-3"/> {live.length} / {MAX_REQUESTS} requests active · select up to {left} more. You pay only after a traveller accepts.
      </div>

      {!canContact && (verifyPrompt || selected.length > 0) && <SenderVerifyCard need="contact"/>}

      {offers.map((d) => (
        <OfferCard key={d.id} delivery={d} busy={busyId === d.id}
                   onAccept={() => act(d.id, "accept", "Offer accepted — complete payment to confirm.")}
                   onDecline={() => act(d.id, "decline")}/>
      ))}

      {loading && trips.length === 0 && <div className="rounded-2xl bg-white border p-6 text-center text-slate-500">Finding travellers near your route…</div>}
      {!loading && trips.length === 0 && offers.length === 0 && <EmptyState parcel={parcel} onRefresh={refresh} loading={loading}/>}

      {trips.filter((t) => !offers.some((o) => o.trip_id === t.id || o.carrier_id === t.carrier_id)).map((t) => (
        <CarrierCard key={t.id} trip={t} selected={selected.includes(t.id)} canSelect={selected.length < left}
                     onToggle={() => toggle(t.id)} onCancel={() => t.my_request && act(t.my_request.id, "withdraw", "Request cancelled.")}/>
      ))}

      {trips.length > 0 && <SendBar count={selected.length} onSend={sendAll} sending={sending} left={left}/>}
    </div>
  );
};

export default BrowseTrips;
